<template>
  <v-dialog v-model="dialog" max-width="900" persistent>
    <v-card v-if="error">
      <v-card-title class="headline primary white--text">
        <v-icon left color="white">mdi-alert-circle</v-icon>
        Detalhes do Erro #{{ error.id }}
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Informações Básicas -->
        <v-row>
          <v-col cols="12">
            <div class="text-h6 mb-2">
              <v-icon left>mdi-information</v-icon>
              Informações Gerais
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>

          <v-col cols="12" md="6">
            <div class="caption grey--text">Tipo de Erro</div>
            <div class="font-weight-bold">{{ error.errorType }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="caption grey--text">Data/Hora</div>
            <div class="font-weight-bold">{{ formatDate(error.errorTimestamp) }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="caption grey--text">Erro de Rede</div>
            <v-chip small :color="error.isNetworkError ? 'error' : 'info'">
              {{ error.isNetworkError ? "Sim" : "Não" }}
            </v-chip>
          </v-col>

          <v-col cols="12" md="6" v-if="error.httpStatus">
            <div class="caption grey--text">Status HTTP</div>
            <v-chip small :color="getStatusColor(error.httpStatus)">
              {{ error.httpStatus }}
            </v-chip>
          </v-col>

          <v-col cols="12" md="6" v-if="error.draftId">
            <div class="caption grey--text">Draft ID</div>
            <div class="font-weight-bold">{{ error.draftId }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="caption grey--text">Tentativas de Retry</div>
            <div class="font-weight-bold">{{ error.retryCount }}</div>
          </v-col>
        </v-row>

        <!-- Mensagem de Erro -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="text-h6 mb-2">
              <v-icon left>mdi-message-alert</v-icon>
              Mensagem de Erro
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>

          <v-col cols="12">
            <v-alert type="error" outlined dense class="text-pre-wrap">
              {{ error.errorMessage }}
            </v-alert>
          </v-col>
        </v-row>

        <!-- Informações do Cliente -->
        <v-row class="mt-2">
          <v-col cols="12">
            <div class="text-h6 mb-2">
              <v-icon left>mdi-laptop</v-icon>
              Informações do Cliente
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>

          <v-col cols="12" md="4" v-if="error.browser">
            <div class="caption grey--text">Browser</div>
            <div class="font-weight-bold">
              <v-icon small left>mdi-web</v-icon>
              {{ error.browser }}
            </div>
          </v-col>

          <v-col cols="12" md="4" v-if="error.os">
            <div class="caption grey--text">Sistema Operacional</div>
            <div class="font-weight-bold">
              <v-icon small left>mdi-monitor</v-icon>
              {{ error.os }}
            </div>
          </v-col>

          <v-col cols="12" md="4" v-if="error.payloadSize">
            <div class="caption grey--text">Tamanho do Payload</div>
            <div class="font-weight-bold">
              <v-icon small left>mdi-package-variant</v-icon>
              {{ formatBytes(error.payloadSize) }}
            </div>
          </v-col>

          <v-col cols="12" v-if="error.userAgent">
            <div class="caption grey--text">User Agent</div>
            <div class="text-caption grey--text text--darken-2">
              {{ error.userAgent }}
            </div>
          </v-col>
        </v-row>

        <!-- Informações Adicionais -->
        <v-row class="mt-2" v-if="error.additionalInfo && Object.keys(error.additionalInfo).length > 0">
          <v-col cols="12">
            <div class="text-h6 mb-2">
              <v-icon left>mdi-information-outline</v-icon>
              Informações Adicionais
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>

          <v-col cols="12">
            <v-card outlined>
              <v-card-text>
                <pre class="text-caption">{{ JSON.stringify(error.additionalInfo, null, 2) }}</pre>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Informações do Terapeuta -->
        <v-row class="mt-2" v-if="error.therapist">
          <v-col cols="12">
            <div class="text-h6 mb-2">
              <v-icon left>mdi-account</v-icon>
              Terapeuta
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>

          <v-col cols="12" md="6">
            <div class="caption grey--text">Nome</div>
            <div class="font-weight-bold">{{ error.therapist.name }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="caption grey--text">Email</div>
            <div class="font-weight-bold">{{ error.therapist.mail }}</div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Fechar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

export default {
  name: "ErrorDetailsDialog",
  data: () => ({
    dialog: false,
    error: null,
  }),
  methods: {
    open(error) {
      this.error = error;
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.error = null;
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("DD/MM/YYYY HH:mm:ss");
    },
    formatBytes(bytes) {
      if (bytes === 0 || !bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
    },
    getStatusColor(status) {
      if (status >= 500) return "error";
      if (status >= 400) return "warning";
      if (status >= 200 && status < 300) return "success";
      return "grey";
    },
  },
};
</script>

<style scoped>
.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
